// Appels HTTP typés vers /api/invitations/* et /api/members/invite.
//
// On regroupe ici :
// - L'appel admin d'invitation (POST /api/members/invite) qui peut
//   renvoyer en mode `direct` (user ajouté) ou `invited` (email envoyé).
// - Le listing / revoke des invitations en cours (admin).
// - Les routes publiques d'acceptation (preview + accept) — non-authentifiées,
//   elles utilisent donc `fetch` direct avec le token en URL, sans auth JWT.
//
// Les schémas TS miroir sont gardés proches des schémas Pydantic backend
// (app/schemas/invitation.py) — à resynchro si le back bouge.

import { api, ApiError } from "./client";
import type { AuthResponse, UUID, UserRole } from "./types";

// ---- Statuts ---------------------------------------------------------------

export type InvitationStatus = "pending" | "accepted" | "revoked";

// ---- Public (admin-visible) -----------------------------------------------

export type InvitationPublic = {
  id: UUID;
  email: string;
  role: UserRole;
  status: InvitationStatus;
  expires_at: string; // ISO
  created_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
};

// ---- Invite member (hybride) ----------------------------------------------

export type InviteMemberPayload = {
  email: string;
  role: UserRole;
};

/**
 * Mini-représentation d'un membre — utilisée quand le destinataire a déjà
 * un compte et qu'on l'ajoute directement au tenant, sans passer par email.
 */
export type MemberPublicRef = {
  user_id: UUID;
  email: string;
  full_name: string;
  role: UserRole;
  is_active: boolean;
  joined_at: string;
  last_login_at: string | null;
};

export type InviteMemberResponse =
  | { mode: "direct"; member: MemberPublicRef; invitation: null }
  | { mode: "invited"; member: null; invitation: InvitationPublic };

// ---- Public preview / accept (non-authentifiés) ----------------------------

export type InvitationPreview = {
  email: string;
  role: UserRole;
  tenant_name: string;
  inviter_name: string | null;
  expires_at: string;
  status: InvitationStatus;
  user_exists: boolean;
};

export type AcceptInvitationPayload = {
  password: string;
  full_name: string;
};

/**
 * Les endpoints publics utilisent un fetch nu (sans auth JWT), sinon le
 * wrapper ajouterait un Authorization bearer inutile et/ou périmé.
 * On reste malgré tout compatible avec `ApiError` pour l'UI.
 */
async function publicRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const resp = await fetch(path, {
    ...init,
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (resp.status === 204) return undefined as T;
  const payload = (await resp.json().catch(() => ({ detail: resp.statusText }))) as {
    detail?: string;
    [k: string]: unknown;
  };
  if (!resp.ok) {
    const detail =
      typeof payload.detail === "string"
        ? payload.detail
        : `Erreur HTTP ${resp.status}`;
    throw new ApiError(resp.status, detail, payload);
  }
  return payload as T;
}

export const invitationsApi = {
  // ---- Admin ----
  inviteMember: (payload: InviteMemberPayload) =>
    api.post<InviteMemberResponse>("/api/members/invite", payload),

  list: () => api.get<InvitationPublic[]>("/api/invitations"),

  revoke: (invitationId: UUID) =>
    api.delete<void>(`/api/invitations/${invitationId}`),

  // ---- Public (non-authentifié) ----
  preview: (token: string) =>
    publicRequest<InvitationPreview>(
      `/api/invitations/${encodeURIComponent(token)}`,
    ),

  accept: (token: string, payload: AcceptInvitationPayload) =>
    publicRequest<AuthResponse>(
      `/api/invitations/${encodeURIComponent(token)}/accept`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
};
